import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useErrorToast } from "@/hooks/use-error-toast";
import { getInvoiceCategoryColor } from "@/lib/invoiceCategories";
import { getInvoiceLabel } from "@/lib/invoiceStatus";
import { pluralizeInvoices } from "@/lib/taskQueue";
import { useHasPermission } from "@/features/auth/useAuth";
import { PERMISSIONS } from "@/features/auth/permissions";
import {
  useGetInvoiceCategoriesQuery,
  useUpdateInvoiceCategoryMutation,
} from "@/services/invoices/invoicesApi";

/** The category radio list, shared by the single-row and bulk dialogs. */
function InvoiceCategoryChoices({ categories, isLoading, selected, onSelect, name = "invoice-category" }) {
  if (isLoading) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" aria-hidden="true" />
        Loading types…
      </p>
    );
  }

  return (
    <fieldset className="invoice-choice-list">
      <legend className="sr-only">Invoice type</legend>
      {categories.map((category) => (
        <label key={category.guid} className="invoice-choice">
          <input
            type="radio"
            name={name}
            value={category.guid}
            checked={selected === category.id}
            onChange={() => onSelect(category.id)}
          />
          <span
            className="invoice-choice-mark"
            style={{ backgroundColor: getInvoiceCategoryColor(category.id) }}
            aria-hidden="true"
          />
          <span className="text-sm text-foreground">{category.name}</span>
        </label>
      ))}
    </fieldset>
  );
}

/**
 * The same picker for a whole selection. It owns no mutation: there is no bulk
 * category endpoint, so the page runs one request per invoice through the
 * queue and reports the tally.
 */
export function InvoiceBulkCategoryModal({ open, onOpenChange, count, onSubmit, isSubmitting }) {
  const { data: categories = [], isLoading } = useGetInvoiceCategoriesQuery(undefined, {
    skip: !open,
  });
  const [selected, setSelected] = useState(null);
  const [wasOpen, setWasOpen] = useState(open);

  // Reset on open rather than in an effect, which would render the previous
  // choice once before clearing it.
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setSelected(null);
  }

  const selectedCategory = categories.find((category) => category.id === selected);

  const submit = (event) => {
    event.preventDefault();
    if (!selectedCategory?.guid) return;
    onSubmit(selectedCategory.guid);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false} className="max-w-sm">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>Change invoice type</DialogTitle>
            <DialogDescription>
              Applies to {pluralizeInvoices(count)}. Each is updated individually, so some may
              succeed while others fail.
            </DialogDescription>
          </DialogHeader>

          <DialogBody>
            <InvoiceCategoryChoices
              categories={categories}
              isLoading={isLoading}
              selected={selected}
              onSelect={setSelected}
              name="bulk-invoice-category"
            />
          </DialogBody>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="h-9 rounded-[0.8rem] border-line shadow-none sm:min-w-24"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              className="h-9 rounded-[0.8rem] sm:min-w-24"
              disabled={isSubmitting || !selectedCategory}
            >
              {isSubmitting ? (
                <>
                  <Loader2 data-icon="inline-start" className="animate-spin" />
                  Updating
                </>
              ) : (
                "Update"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Reclassifies an invoice (Invoice / Credit Note / Sample). Reached by clicking
 * the category dot in the first column.
 *
 * The API keys the category by guid on write but by id on read, so both are
 * tracked here rather than converting by name the way the old app did.
 */
export function InvoiceCategoryModal({ invoice, open, onOpenChange }) {
  const showErrorToast = useErrorToast();
  const canReadCategories = useHasPermission()(PERMISSIONS.INVOICE_CATEGORIES);
  const { data: categories = [], isLoading } = useGetInvoiceCategoriesQuery(undefined, {
    skip: !open || !canReadCategories,
  });
  const [updateInvoiceCategory, { isLoading: isSaving }] = useUpdateInvoiceCategoryMutation();

  const [selected, setSelected] = useState(null);
  const [seededGuid, setSeededGuid] = useState(null);

  // Closing forgets the seed, so reopening the same invoice starts from its type
  // as it is now rather than from a choice cancelled earlier.
  if (!open && seededGuid !== null) {
    setSeededGuid(null);
  } else if (open && invoice && invoice.id !== seededGuid) {
    setSeededGuid(invoice.id);
    setSelected(invoice.categoryId ?? null);
  }

  const selectedCategory = categories.find((category) => category.id === selected);

  const submit = async (event) => {
    event.preventDefault();
    if (!selectedCategory?.guid) return;

    try {
      await updateInvoiceCategory({
        guid: invoice.id,
        categoryGuid: selectedCategory.guid,
      }).unwrap();
      toast.success("Invoice type updated");
      onOpenChange(false);
    } catch (error) {
      showErrorToast(error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false} className="max-w-sm">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>Change invoice type</DialogTitle>
            <DialogDescription>
              {invoice
                ? `${getInvoiceLabel(invoice)} · currently ${invoice.categoryName ?? "unclassified"}`
                : null}
            </DialogDescription>
          </DialogHeader>

          <DialogBody>
            <InvoiceCategoryChoices
              categories={categories}
              isLoading={isLoading}
              selected={selected}
              onSelect={setSelected}
            />
          </DialogBody>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="h-9 rounded-[0.8rem] border-line shadow-none sm:min-w-24"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              className="h-9 rounded-[0.8rem] sm:min-w-24"
              disabled={isSaving || !selectedCategory || selected === invoice?.categoryId}
            >
              Update
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
